import { Link } from 'react-router-dom';

const LAST_UPDATED = 'February 27, 2026';
const COMPANY = 'Outlaw Oasis';
const SITE_URL = 'https://www.outlawoasis.com';
const CONTACT_EMAIL = 'support@outlawoasis.com';

export default function Refunds() {
  return (
    <div className="py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-12 text-center">
          <h1 className="font-western text-4xl sm:text-5xl text-gold mb-4">Refund Policy</h1>
          <p className="text-text-muted text-sm">Last updated: {LAST_UPDATED}</p>
        </div>

        <div className="space-y-10 text-text-secondary text-sm leading-relaxed">

          {/* Key Summary Box */}
          <div className="p-5 bg-leather rounded-lg border border-gold/30">
            <h2 className="text-cream font-semibold text-base mb-2">Summary</h2>
            <ul className="space-y-1.5">
              <li className="flex items-start gap-2">
                <span className="text-hot mt-0.5">✕</span>
                <span>{COMPANY} does <strong className="text-cream">not</strong> offer refunds on any digital product or service.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gold mt-0.5">◈</span>
                <span>In limited cases, at our sole discretion, we may offer a <strong className="text-cream">replacement</strong> of equivalent or similar value.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-hot mt-0.5">✕</span>
                <span>Bans, detection, or enforcement actions by game developers are <strong className="text-cream">not</strong> grounds for a replacement.</span>
              </li>
            </ul>
          </div>

          {/* 1 */}
          <section>
            <h2 className="text-xl font-semibold text-cream mb-3 border-b border-gold/20 pb-2">
              1. All Sales Are Final
            </h2>
            <p>
              Due to the instant and digital nature of our products — including game accounts, cheat
              software, digital keys, and other digital goods — <strong className="text-cream">all
              sales on {COMPANY} are final and non-refundable</strong>. Once a digital product has been
              delivered or made accessible to the buyer, we cannot undo or reverse the transaction.
            </p>
            <p className="mt-3">
              By completing a purchase on {COMPANY}, you explicitly acknowledge and agree that:
            </p>
            <ul className="list-disc list-inside mt-3 space-y-2 ml-2">
              <li>You understand the nature of the digital product you are purchasing</li>
              <li>Digital delivery constitutes fulfillment of the order</li>
              <li>You waive any right to a refund upon delivery or access to the product</li>
              <li>You have read and understood the full product description and any associated risks prior to purchase</li>
            </ul>
          </section>

          {/* 2 */}
          <section>
            <h2 className="text-xl font-semibold text-cream mb-3 border-b border-gold/20 pb-2">
              2. Replacement Policy
            </h2>
            <p>
              While refunds are not available, {COMPANY} may, entirely at our sole and absolute
              discretion, offer a <strong className="text-cream">replacement product</strong> of
              equivalent or similar value in exceptional circumstances. The decision to offer a
              replacement rests solely with {COMPANY} and is not guaranteed.
            </p>
            <p className="mt-3">
              A replacement may be considered in the following circumstances only:
            </p>
            <ul className="list-disc list-inside mt-3 space-y-2 ml-2">
              <li>
                <strong className="text-cream">Non-delivery:</strong> The product was not delivered
                or made accessible within the stated timeframe and the seller has not responded to
                resolution attempts.
              </li>
              <li>
                <strong className="text-cream">Materially incorrect product:</strong> The delivered
                product is substantially and verifiably different from what was described in the
                listing (e.g., wrong game, wrong account tier).
              </li>
              <li>
                <strong className="text-cream">Non-functional on delivery:</strong> The product was
                confirmed non-functional at the time of delivery (e.g., an account that was already
                banned before the sale, or software with no valid license key) and this is verified
                by our team.
              </li>
              <li>
                <strong className="text-cream">Seller fraud:</strong> The seller is found to have
                engaged in fraudulent or deceptive practices in connection with your specific order.
              </li>
            </ul>
          </section>

          {/* 3 */}
          <section>
            <h2 className="text-xl font-semibold text-cream mb-3 border-b border-gold/20 pb-2">
              3. What Is NOT Eligible for Replacement
            </h2>
            <p>
              The following situations are explicitly <strong className="text-cream">not</strong>{' '}
              eligible for a replacement or any other remedy:
            </p>
            <ul className="list-disc list-inside mt-3 space-y-2 ml-2">
              <li>
                <strong className="text-cream">Account bans by game developers or publishers.</strong>{' '}
                Game developers may detect and ban accounts at any time, including after a purchase is
                completed. This is a known and accepted risk of purchasing game accounts and does not
                entitle you to a replacement.
              </li>
              <li>
                <strong className="text-cream">Cheat detection or anti-cheat bans.</strong>{' '}
                Cheat software may be detected by anti-cheat systems (e.g., VAC, EAC, BattlEye) at
                any time following an update or patch. Detection and any resulting bans are an
                inherent risk of using such software and are not eligible for replacement.
              </li>
              <li>
                <strong className="text-cream">Change of mind.</strong> Dissatisfaction with a
                product that was accurately described and correctly delivered does not constitute
                grounds for a replacement.
              </li>
              <li>
                <strong className="text-cream">Account banned after delivery.</strong> If an account
                was functional and correctly described at the time of delivery but later banned by a
                game developer, this is not eligible for replacement.
              </li>
              <li>
                <strong className="text-cream">User-caused issues.</strong> Issues arising from
                your own actions — such as getting an account banned by violating game rules yourself,
                forgetting login credentials after delivery, or using a cheat incorrectly — are not
                eligible for replacement.
              </li>
              <li>
                <strong className="text-cream">Game patches or software updates.</strong> If a
                cheat or software product stops working due to a game update, patch, or anti-cheat
                update after delivery, this is not eligible for replacement.
              </li>
              <li>
                <strong className="text-cream">Policy violations by the buyer.</strong> If your
                account or order is flagged for violating our Terms of Service, you are not eligible
                for any replacement.
              </li>
            </ul>
          </section>

          {/* 4 */}
          <section>
            <h2 className="text-xl font-semibold text-cream mb-3 border-b border-gold/20 pb-2">
              4. How to Request a Replacement
            </h2>
            <p>
              If you believe your situation may qualify for a replacement under Section 2, you must:
            </p>
            <ol className="list-decimal list-inside mt-3 space-y-2 ml-2">
              <li>
                Submit a support ticket through our{' '}
                <Link to="/support" className="text-gold hover:underline">Support page</Link>{' '}
                within <strong className="text-cream">72 hours</strong> of delivery or the expected
                delivery time.
              </li>
              <li>
                Include your <strong className="text-cream">order number</strong>, a clear description
                of the issue, and any supporting evidence (screenshots, screen recordings, etc.).
              </li>
              <li>
                Cooperate with our support team's investigation. We may request additional information
                or evidence to process your request.
              </li>
            </ol>
            <p className="mt-3">
              Requests submitted after 72 hours of delivery will not be considered. We aim to respond
              to all replacement requests within 3–5 business days.
            </p>
            <p className="mt-3">
              The outcome of any replacement request is at the sole discretion of {COMPANY}. Our
              decision is final.
            </p>
          </section>

          {/* 5 */}
          <section>
            <h2 className="text-xl font-semibold text-cream mb-3 border-b border-gold/20 pb-2">
              5. Chargebacks &amp; Payment Disputes
            </h2>
            <p>
              Initiating a chargeback or payment dispute with your bank or payment provider without
              first contacting {COMPANY} through our official support channels is considered a
              violation of our Terms of Service. We reserve the right to:
            </p>
            <ul className="list-disc list-inside mt-3 space-y-2 ml-2">
              <li>Immediately suspend or permanently ban your account</li>
              <li>Dispute the chargeback and provide evidence to the payment processor</li>
              <li>Pursue recovery of funds through legal or collection means</li>
              <li>Report fraudulent chargeback activity to relevant authorities</li>
            </ul>
            <p className="mt-3">
              If you have a legitimate concern about a charge, please contact our support team first.
              We are committed to resolving genuine disputes fairly and promptly.
            </p>
          </section>

          {/* 6 */}
          <section>
            <h2 className="text-xl font-semibold text-cream mb-3 border-b border-gold/20 pb-2">
              6. Seller Disputes
            </h2>
            <p>
              If you have a dispute with a seller, please contact our support team. We will investigate
              and mediate disputes between buyers and sellers in accordance with our marketplace policies.
              {COMPANY} reserves the right to make final decisions in all seller-buyer disputes.
            </p>
          </section>

          {/* 7 */}
          <section>
            <h2 className="text-xl font-semibold text-cream mb-3 border-b border-gold/20 pb-2">
              7. Changes to This Policy
            </h2>
            <p>
              We reserve the right to update this Refund Policy at any time. Changes will be reflected
              by updating the "Last updated" date at the top of this page. Your continued use of the
              platform after any changes constitutes acceptance of the updated policy.
            </p>
          </section>

          {/* 8 */}
          <section>
            <h2 className="text-xl font-semibold text-cream mb-3 border-b border-gold/20 pb-2">
              8. Contact Us
            </h2>
            <p>
              If you have questions about this Refund Policy or wish to submit a replacement request,
              please contact us through our Support page or by email:
            </p>
            <div className="mt-3 p-4 bg-leather rounded-lg border border-gold/20">
              <p className="text-cream font-semibold">{COMPANY} Support</p>
              <p className="mt-1">
                Email:{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-gold hover:underline">
                  {CONTACT_EMAIL}
                </a>
              </p>
              <p>
                Support portal:{' '}
                <Link to="/support" className="text-gold hover:underline">
                  {SITE_URL}/support
                </Link>
              </p>
            </div>
          </section>

          {/* Related links */}
          <div className="pt-6 border-t border-gold/10 flex flex-wrap gap-4 text-sm">
            <Link to="/terms" className="text-gold hover:underline">Terms of Service</Link>
            <Link to="/privacy" className="text-gold hover:underline">Privacy Policy</Link>
            <Link to="/support" className="text-gold hover:underline">Contact Support</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
